// Offline Support (120) -- browser online/offline state.
//
// Thin wrapper over `navigator.onLine` + the `online`/`offline` window events.
// `navigator.onLine` is necessary-but-not-sufficient (it only knows the link is
// up, not that the API is reachable), so callers treat "offline" as a strong
// signal and "online" as a prompt to retry, not a guarantee. See decision 3.

import { useSyncExternalStore } from "react";

type Listener = () => void;

const listeners = new Set<Listener>();
let started = false;

function emit() {
  for (const fn of listeners) fn();
}

function ensureStarted() {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("online", emit);
  window.addEventListener("offline", emit);
}

function subscribe(fn: Listener): () => void {
  ensureStarted();
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function getSnapshot(): boolean {
  // Default to online when the API is unavailable (SSR / tests) so we never
  // wrongly block writes.
  if (typeof navigator === "undefined") return true;
  return navigator.onLine;
}

/** Imperative read of current connectivity (use outside React, e.g. in api.ts). */
export function isOnline(): boolean {
  return getSnapshot();
}

/** Register a callback fired whenever connectivity flips. Returns an unsubscribe. */
export function onConnectivityChange(fn: (online: boolean) => void): () => void {
  return subscribe(() => fn(getSnapshot()));
}

/** React hook: re-renders when connectivity changes. */
export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => true);
}
